import { Dispatch, SetStateAction } from 'react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { InvoiceModel } from '@frontend/api-invoices';
import { Merchant } from '@frontend/api-payments';
import { contextFactory, MultiStepControl } from '@frontend/design-system';
import { CareCreditPatientData, FinanceOption } from '../../../types/care-credit.types';
import {
  CareCreditCollectSubmissionStatus,
  CollectFormFieldNames,
  CollectFormState,
} from '../collect-multi-step.types';

export type CareCreditCollectContextState = {
  person: Person | undefined;
  merchant: Merchant | undefined;
  locationId: string | undefined;
  invoice: InvoiceModel | undefined;
  careCreditData: CareCreditPatientData | undefined;
  hideBackButtonOnCareCreditCollect?: boolean;
  collectForm: CollectFormState;
  financeOptions: FinanceOption[];
  isLoadingFinanceOptions: boolean;
  selectedFinanceOption: FinanceOption | undefined;
  isLoading: boolean; // has person, has CareCredit data
  isSubmitting: boolean;
  multiStep: MultiStepControl;

  setIsSubmitting: Dispatch<SetStateAction<boolean>>;

  onGoToMessageStep: () => void;

  onChangeFormField: (field: CollectFormFieldNames, value: string, formIsValid: boolean) => void;

  setSelectedFinanceOption: React.Dispatch<React.SetStateAction<FinanceOption | undefined>>;

  onRefetchInvoice: () => Promise<InvoiceModel | undefined>;

  /**
   * Leave all flows entirely / close modal
   */
  onCancel: (() => void) | undefined;

  /**
   * Leave CareCredit Collection flow.
   * (i.e go back to payment methods step)
   */
  onGoBack: () => void;

  /**
   * Called when submission is complete
   */
  onSubmissionComplete: (status: CareCreditCollectSubmissionStatus) => void;
};

export const [CareCreditCollectContext, useCareCreditCollectContext] = contextFactory<CareCreditCollectContextState>(
  '',
  {
    person: undefined,
    invoice: undefined,
    merchant: undefined,
    locationId: undefined,
    careCreditData: undefined,
    multiStep: {} as MultiStepControl,
    collectForm: {
      fields: {
        accountHolderName: '',
        purchaseAmount: '',
        serviceCompletionDate: '',
        primaryIdType: '',
        issuingState: '',
        expirationDate: '',
      },
      isValid: false,
    },
    financeOptions: [],
    isLoadingFinanceOptions: false,
    selectedFinanceOption: undefined,
    isLoading: false,
    isSubmitting: false,
    setSelectedFinanceOption: () => {},
    onChangeFormField: () => {},
    onGoToMessageStep: () => {},
    onRefetchInvoice: () => Promise.resolve(undefined),
    onSubmissionComplete: () => {},
    setIsSubmitting: () => {},
    onCancel: () => {},
    onGoBack: () => {},
  }
);
